import React from "react"
import {Resource} from "../../resources/common"
import moment from "moment"
import numeral from "numeral"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {useNavigate} from "react-router-dom"
import {OrgName} from "../OrgName/OrgName"
import ReactTooltip from "react-tooltip"
import BodyPortal from "../BodyPortal/BodyPortal"
import {KeyValueCard, KeyValueCardContainer, KeyValueCardKey, KeyValueCardValue} from "../KeyValueCard/KeyValueCard"
import Button from "../Button/Button"

export const checkDepositTooltipValue =
	"The funds from checks that are deposited before the daily cutoff, are received by Unit on the next business day, and held for the clearing period defined on the account’s deposit product"

interface CheckDepositDetailsProps {
	amount: number
	number?: string
	customerId?: number
	accountId?: number
	depositDate: Date
	description: string
	include?: Resource[]
	settlementDate?: Date
	orgId?: string
	tags?: {[key: string]: string}
	showUpdateModal?: () => void | undefined
}

export function CheckDepositDetails({
	amount,
	number,
	customerId,
	orgId,
	accountId,
	depositDate,
	description,
	include,
	settlementDate,
	tags,
	showUpdateModal = undefined,
}: CheckDepositDetailsProps) {
	const navigate = useNavigate()
	const customer =
		customerId && include
			? include.find(
					(r) =>
						(r.type == "individualCustomer" || r.type == "businessCustomer" || r.type == "businessFBOCustomer") &&
						r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer" || customer.type == "businessFBOCustomer") {
			customerName = customer.attributes.name
		}
	}

	const updateCheckActionButton = (
		<Button variant="outline" size="normal" onClick={showUpdateModal}>
			Edit Details for X9
		</Button>
	)

	return (
		<KeyValueCard title={"Check Details"} additionalHeaderContent={updateCheckActionButton}>
			<KeyValueCardContainer>
				{amount && (
					<>
						<KeyValueCardKey> Check Amount</KeyValueCardKey>
						<KeyValueCardValue> {numeral(amount / 100).format("$0,0.00")} </KeyValueCardValue>
					</>
				)}

				{number && (
					<>
						<KeyValueCardKey> Check Number</KeyValueCardKey>
						<KeyValueCardValue> {number} </KeyValueCardValue>
					</>
				)}

				{orgId && (
					<>
						<KeyValueCardKey> Org</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<OrgName orgId={orgId.toString()} element={"span"} />
						</KeyValueCardValue>
					</>
				)}

				{customerName && customer && (
					<>
						<KeyValueCardKey> Customer</KeyValueCardKey>
						<KeyValueCardValue>
							<a
								className="link"
								href={`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`}
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
								}}
							>
								{customerName}
							</a>
						</KeyValueCardValue>
					</>
				)}

				{accountId && (
					<>
						<KeyValueCardKey> Account ID</KeyValueCardKey>
						<KeyValueCardValue>
							<a
								className="link"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									if (accountId) {
										navigate(`/accounts/${accountId}`)
									}
								}}
							>
								{accountId}
							</a>
						</KeyValueCardValue>
					</>
				)}

				{depositDate && (
					<>
						<KeyValueCardKey> Deposit Date</KeyValueCardKey>
						<KeyValueCardValue> {moment(depositDate).format("MM/DD/YYYY h:mm A")} </KeyValueCardValue>
					</>
				)}

				{settlementDate && (
					<>
						<KeyValueCardKey> Expected Completion Date</KeyValueCardKey>
						<KeyValueCardValue>
							<span
								data-tip="React-tooltip"
								data-for={`check-deposit-drawer-expected-completion-time-tooltip-${number}`}
							>
								{moment(settlementDate).format("MM/DD/YYYY")}
							</span>
							<BodyPortal>
								<ReactTooltip
									className="tooltip-info"
									id={`check-deposit-drawer-expected-completion-time-tooltip-${number}`}
									disable={!settlementDate}
									effect="solid"
								>
									{checkDepositTooltipValue}
								</ReactTooltip>
							</BodyPortal>
						</KeyValueCardValue>
					</>
				)}

				{tags && (
					<>
						<KeyValueCardKey> Tags</KeyValueCardKey>
						<KeyValueCardValue>
							<TagsViewerKeyValue tags={tags} />
						</KeyValueCardValue>
					</>
				)}

				{description && (
					<>
						<KeyValueCardKey> Description</KeyValueCardKey>
						<KeyValueCardValue> {description} </KeyValueCardValue>
					</>
				)}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}
